'use client';

import { useCallback, useEffect, useState } from 'react';
import type { DirecionamentoOpcao, NegociacaoPendenciaItem } from '../../../types/negociacaoDetailTypes';
import { useToast } from '@/shared/providers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { FeatherLoader, FeatherSparkles, FeatherTriangleAlert } from '@subframe/core';
import { selecionarDirecionamento } from '../../../store/actions';
import { selectLexiProcessing, selectLexiProcessingFailed, selectNegociacaoDetailCaso } from '../../../store/selectors';
import { clearLexiProcessing } from '../../../store/slice';
const DIRECIONAMENTO_TYPE = 'DIRECIONAMENTO_RETOMADA';
const DEFAULT_TITULO = 'O agente de IA precisa de uma direção';
const DEFAULT_DESCRICAO = 'Após a intervenção humana, o agente analisou o histórico da conversa mas não identificou uma ação clara para continuar. Selecione uma das opções abaixo para definir como o atendimento deve prosseguir.';
const LEXI_PROCESSING_TIMEOUT_MS = 45_000;
const hasOpcoes = (pendencia: NegociacaoPendenciaItem): boolean => Array.isArray(pendencia.payload?.opcoes) && pendencia.payload.opcoes.length > 0;
const findDirecionamentoPendencia = (pendencias: NegociacaoPendenciaItem[]): NegociacaoPendenciaItem | null => pendencias.find(pendencia => pendencia.type === DIRECIONAMENTO_TYPE && hasOpcoes(pendencia)) ?? null;
export function CardDirecionamentoLexi() {
  const dispatch = useAppDispatch();
  const {
    showError
  } = useToast();
  const caso = useAppSelector(selectNegociacaoDetailCaso);
  const lexiProcessing = useAppSelector(selectLexiProcessing);
  const lexiProcessingFailed = useAppSelector(selectLexiProcessingFailed);
  const [submittingOptionId, setSubmittingOptionId] = useState<string | null>(null);
  const pendencia = caso ? findDirecionamentoPendencia(caso.pendencias) : null;
  const handleSelect = useCallback(async (opcao: DirecionamentoOpcao) => {
    if (!caso || !pendencia) return;
    setSubmittingOptionId(opcao.id);
    try {
      await dispatch(selecionarDirecionamento({
        caseId: caso.id,
        pendenciaId: pendencia.id,
        selectedOptionId: opcao.id
      })).unwrap();
    } catch (err) {
      showError('Erro', err instanceof Error ? err.message : 'Erro ao registrar direcionamento');
    } finally {
      setSubmittingOptionId(null);
    }
  }, [caso, dispatch, pendencia, showError]);
  useEffect(() => {
    if (!lexiProcessing) return;
    const timer = setTimeout(() => dispatch(clearLexiProcessing()), LEXI_PROCESSING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [lexiProcessing, dispatch]);
  if (caso && pendencia?.payload) {
    const {
      titulo,
      descricao,
      opcoes
    } = pendencia.payload;
    return <div className="flex w-full overflow-hidden rounded-[16px] border border-solid border-neutral-200 bg-neutral-50">
        <div className="bg-brand-700 w-[6px] shrink-0 self-stretch" />
        <div className="flex flex-1 flex-col gap-6 p-5">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] border border-solid border-neutral-200 bg-white shadow-sm">
                <FeatherSparkles className="text-brand-700 h-5 w-5" />
              </div>
              <span className="text-default-font flex-1 text-[18px] font-medium">
                {titulo ?? DEFAULT_TITULO}
              </span>
            </div>
            <span className="text-subtext-color text-[16px]">{descricao ?? DEFAULT_DESCRICAO}</span>
          </div>

          <div className="flex flex-wrap gap-4">
            {opcoes.map(opcao_0 => {
            const isSubmitting = submittingOptionId === opcao_0.id;
            return <button key={opcao_0.id} type="button" onClick={() => handleSelect(opcao_0)} disabled={submittingOptionId !== null} className="text-default-font hover:border-brand-300 inline-flex items-center gap-2 rounded-full border border-solid border-neutral-200 bg-white px-3 py-2 text-[14px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60">
                  {isSubmitting ? <FeatherLoader className="h-4 w-4 animate-spin" /> : null}
                  {opcao_0.label}
                </button>;
          })}
          </div>
        </div>
      </div>;
  }
  if (lexiProcessingFailed) {
    return <div className="border-warning-200 bg-warning-50 flex w-full items-center gap-3 rounded-[16px] border border-solid p-4">
        <FeatherTriangleAlert className="text-warning-700 h-5 w-5 shrink-0" />
        <span className="text-default-font text-[14px]">
          A Lexi não conseguiu retomar o atendimento. Recarregue a conversa ou assuma manualmente.
        </span>
      </div>;
  }
  if (lexiProcessing) {
    return <div className="flex w-full items-center gap-3 rounded-[16px] border border-solid border-neutral-200 bg-neutral-50 p-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-solid border-neutral-200 bg-white">
          <FeatherSparkles className="text-brand-700 h-4 w-4 animate-pulse" />
        </div>
        <div className="flex flex-col">
          <span className="text-default-font text-[14px] font-medium">
            A Lexi está analisando o atendimento…
          </span>
          <span className="text-subtext-color text-[13px]">
            Pode continuar — avisamos aqui e na campainha quando ela responder.
          </span>
        </div>
      </div>;
  }
  return null;
}